import { Button, Table as ChakraTable, Flex, Input, InputGroup, InputRightElement, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Spinner, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";

import { BsSearch } from "react-icons/bs";
import { FaPlus } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import useProduct from "./hooks/useProduct";
import { OlaggModule, PermissionEnum, Product } from "@olagg/db-types";
import useRoles from "../../../features/Roles/hooks/useRoles";
import Pagination from "@olagg/ui-kit/Pagination";
import { ROUTES } from "../../../consts/routes";

const Actions = ({ product, onRefresh }: { product: Product, onRefresh: () => void }) => {
  const { missingPermission } = useRoles();
  const navigate = useNavigate();
  const { deleteProduct, generateCodes, exportCodes } = useProduct();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [quantity, setQuantity] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const viewProduct = (product: Product) => navigate(`/products/${product.id}`);

  const duplicateProduct = (product: Product) => {
    navigate(`/products/crear?duplicate=${product.id}`);
  };

  const removeProduct = (product: Product) => {
    if (window.confirm('¿Estás seguro que deseas eliminar este producto?')) {
      deleteProduct(product.id);
    }
  }

  const handleGenerateCodes = async () => {
    if (quantity > 0) {
      setIsGenerating(true);
      try {
        await generateCodes(product.id, quantity);
        onClose();
        setQuantity(1);
        onRefresh();
      } catch (error) {
        console.error('Error generating codes:', error);
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const handleExportCodes = async () => {
    setIsExporting(true);
    try {
      await exportCodes(product.id);
    } catch (error) {
      console.error('Error exporting codes:', error);
    } finally {
      setIsExporting(false);
    }
  };

  // Verificar si el producto tiene códigos/cupones
  // Asumiendo que el producto tiene una propiedad que indica la cantidad de códigos
  const hasCodesAvailable = product.codes && product.codes.length > 0;

  return (
    <>
      <Flex gap='8px' wrap="wrap">
        <Button 
          isDisabled={missingPermission(OlaggModule.Product, PermissionEnum.EDIT)} 
          bgColor={'#37A169'} 
          p='0 8px' 
          h='30px' 
          fontSize={'12px'} 
          onClick={() => viewProduct(product)}
          size="sm"
        >
          Ver
        </Button>

        <Button 
          isDisabled={missingPermission(OlaggModule.Product, PermissionEnum.CREATE)} 
          bgColor={'#F56500'} 
          p='0 8px' 
          h='30px' 
          fontSize={'12px'}
          onClick={() => duplicateProduct(product)}
          size="sm"
        >
          Duplicar
        </Button>

        <Button 
          isDisabled={missingPermission(OlaggModule.Product, PermissionEnum.EDIT)} 
          bgColor={'#3182CE'} 
          p='0 8px' 
          h='30px' 
          fontSize={'12px'}
          onClick={onOpen}
          size="sm"
        >
          Generar Cupones
        </Button>

        <Button 
          isDisabled={missingPermission(OlaggModule.Product, PermissionEnum.EDIT) || !hasCodesAvailable} 
          bgColor={'#805AD5'} 
          p='0 8px' 
          h='30px' 
          fontSize={'12px'}
          onClick={handleExportCodes}
          isLoading={isExporting}
          loadingText="Exportando"
          size="sm"
        >
          Exportar Cupones
        </Button>

        <Button 
          isDisabled={missingPermission(OlaggModule.Product, PermissionEnum.DELETE)} 
          bgColor={'#E53E3E'} 
          p='0 8px' 
          h='30px' 
          fontSize={'12px'} 
          onClick={() => removeProduct(product)}
          size="sm"
        >
          Eliminar
        </Button>
      </Flex>

      {/* Modal para generar códigos */}
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Generar Códigos para {product.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>¿Cuántos códigos deseas generar?</Text>
            <NumberInput 
              value={quantity} 
              onChange={(valueString) => setQuantity(parseInt(valueString) || 1)}
              min={1}
              max={1000}
            >
              <NumberInputField bg="gray.700" border="1px solid" borderColor="gray.600" />
              <NumberInputStepper>
                <NumberIncrementStepper color="white" />
                <NumberDecrementStepper color="white" />
              </NumberInputStepper>
            </NumberInput>
            <Text fontSize="sm" color="gray.400" mt={2}>
              Cantidad actual de códigos: {product.codes.length || 0}
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleGenerateCodes}
              isLoading={isGenerating}
              loadingText="Generando"
              isDisabled={quantity < 1}
            >
              Generar {quantity} código{quantity !== 1 ? 's' : ''}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

const ProductsList = () => {
  const { products, all, isLoading, count } = useProduct();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [filter, setFilter] = useState({
      name: searchParams.get('name') || '',
      page: Number(searchParams.get('page') || 1),
      limit: Number(searchParams.get('limit') || 10)
    });

    const onFilter = ({
      name,
      value
    }: EventTarget & HTMLInputElement) => {
      setFilter({ ...filter, [name]: value })

      if (value) searchParams.set(name, value)
      else searchParams.delete(name)
      setSearchParams(searchParams)
    }

    const refreshProducts = () => {
      all(filter);
    };

    const { page, limit, name } = filter;

    useEffect(() => {
      all(filter);
    }, [filter]);

  return (
    <Flex gap={5} direction="column" mt={4}>
      <Flex flexDirection={'row'} justify="space-between" pr={6}>
        <Flex flex="1">
          <InputGroup mx={5} w="50%">
            <Input
              color={"white"}
              placeholder="Nombre, Nombre en Bonox, ID de Bonox"
              name="name"
              value={name}
              onChange={(e) => onFilter(e.target)}
            />
            <InputRightElement children={<BsSearch color="white" />} />
          </InputGroup>
        </Flex>
        <Button
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.PRODUCTS.create)}
            mb='3'
        >
            Crear Producto
        </Button>
      </Flex>

      <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
        <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
          <ChakraTable
            variant="striped"
            size="sm"
            colorScheme="olaggPurple"
            fontSize="18px"
            position='relative'
            whiteSpace={'normal'}
          >
            <Thead>
              <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                <Th py={'20px'} color="white">ID</Th>
                <Th py={'20px'} color="white">Bonox ID</Th>
                <Th py={'20px'} color="white">Nombre</Th>
                <Th py={'20px'} color="white">Face Value</Th>
                <Th py={'20px'} color="white">Region</Th>
                <Th py={'20px'} color="white">Precio de Venta (Calculado)</Th>
                <Th py={'20px'} color="white">Precio de Revendedor</Th>
                <Th py={'20px'} color="white">Orden de Visualización</Th>
                <Th py={'20px'} color="white">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {products.map((product, index) => (
                <Tr key={index} color="white">
                  <Td py={'20px'}>{product?.id}</Td>
                  <Td py={'20px'}>{product?.referenceId}</Td>
                  <Td py={'20px'}>{product?.name}</Td>
                  <Td py={'20px'}>{product?.faceValue}</Td>
                  <Td py={'20px'}>{product?.redeemableRegion}</Td>
                  <Td py={'20px'}>{product?.sellingPrice}</Td>
                  <Td py={'20px'}>{product?.merchantPrice}</Td>
                  <Td py={'20px'}>{product?.order}</Td>
                  <Td py={'20px'}>
                    <Actions product={product} onRefresh={refreshProducts} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </ChakraTable>
        </TableContainer>
      </Flex>
      <Flex px={'20px'} w='full' justifyContent={'center'}>
        <Pagination
          params={{limit, page, count}}
          onClick={(newPage) => 
            onFilter({
              name: 'page',
              value: String(newPage + 1)
            } as EventTarget & HTMLInputElement)
          }
          onLimitChange={(newLimit) => 
            onFilter({
              name: 'limit',
              value: newLimit
            } as EventTarget & HTMLInputElement)
          }
          hideLimitInput
        />
      </Flex>
    </Flex>
  )
}

export default ProductsList;